import {
  isLargeLeadQueryByAdults,
  LARGE_LEAD_ADULTS_THRESHOLD,
  PAGINATION,
  type LeadBucket,
  type LeadListItem,
  type LeadListQuery,
  type LeadPriority,
} from '@utazzo/shared';
import {
  UzAvatar,
  UzButton,
  UzCombobox,
  UzInput,
  UzSelect,
  UzSpinner,
  UzTooltip,
} from '@utazzo/ui';
import { Check, ListChecks, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { canBulkAssignLeads } from '../bulk-assign-access';
import { CARRYFORWARD_FLOOR_ISO, getPrevMonthEndIso } from '../carryforward';
import {
  isoDate,
  istIsoDate,
  type LeadDateRangePresetKey,
  parsePresetKey,
  resolveLeadDateRange,
} from '../lead-date-range';
import { resolveStageHex, stageDotStyle } from '../stage-color';
import {
  useDestinationOptionsForLeads,
  useQueryStatusOptionsWithBucket,
  useQuerySubStatusOptionsForParent,
} from '../use-lead-options';
import { useLeadsList } from '../use-leads';

import { LeadBulkReassignDialog } from './LeadBulkReassignDialog';
import { OwnerFilter } from './OwnerFilter';

/**
 * Lead Intent (stored on the `priority` column) filter for the inbox aside.
 * `ANY` is the default no-filter sentinel - Radix Select disallows an empty
 * string value, so we map it to "no priority filter" in the query builder.
 */
const INTENT_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'ANY', label: 'Any intent' },
  { value: 'URGENT', label: 'Urgent' },
  { value: 'HIGH', label: 'High' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'LOW', label: 'Low' },
];

import { useCurrentUser } from '@/features/auth/use-auth';
import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

interface LeadInboxAsideProps {
  selectedLeadId: string | null;
  onSelect: (lead: LeadListItem) => void;
}

/**
 * 2026-07-21. Aside date-range vocabulary + widget are now unified with
 * the Lead Inbox header. The `DateRangeFilter` component (calendar +
 * preset column) drives both, so the two ALWAYS show the same picker,
 * same preset list, same custom-range calendar behaviour. Both write
 * to the shared `?daterange=` URL param and honour the same admin-only
 * RBAC gate. Legacy `?range=` bookmarks are read once at mount
 * (migrated in-memory to the equivalent preset key); the first
 * subsequent URL sync drops the deprecated param so old links keep
 * working without loitering.
 */

/**
 * Aside default range. Aligned with the inbox default
 * (`DEFAULT_RANGE_PRESET = 'this_month'`) so both surfaces answer the
 * same question - "leads created in this month" - by default. Before
 * 2026-07-21 the aside used `last_30_days`, which created a silent
 * mismatch for non-admin users: they land on `/leads/{id}` with the
 * URL locked to the aside default (canChangeAsideRange gate) and
 * their inbox showed `this_month` counts while the aside used a
 * rolling 30-day window - two different numbers for the same data set.
 * Using the same preset removes that class of drift entirely.
 */
const DEFAULT_ASIDE_RANGE: LeadDateRangePresetKey = 'this_month';

/// Legacy `?range=` -> unified `?daterange=` migration map. Old bookmarks
/// keep working; the aside rewrites the URL to the new key on next state
/// change so the deprecated param falls off naturally.
const LEGACY_RANGE_MIGRATION: Record<string, LeadDateRangePresetKey> = {
  today: 'today',
  '7': 'last_7_days',
  '30': 'last_30_days',
  '90': 'last_90_days',
  all: 'all',
};

/**
 * 2026-07-17. Aside list pagination: the list shows one "page" of rows
 * and a "Load more" button pulls in the next step. Implemented by
 * growing the query's `pageSize` (page stays 1), so each click
 * refetches the whole visible slice - React Query caches by key and
 * slices this small are cheap, while keeping the row set stable (no
 * page-2 stitching / dedupe). Growth is capped at the API's
 * MAX_PAGE_SIZE; past that the operator should narrow the filters.
 */
const LIST_PAGE_STEP = 15;

/**
 * Read the aside's range preset from URL. Priority:
 *   1. `?daterange=` (unified, matches Lead Inbox header)
 *   2. `?range=` (legacy - migrated via `LEGACY_RANGE_MIGRATION`)
 *   3. Aside default (`DEFAULT_ASIDE_RANGE`).
 */
const parseRange = (params: URLSearchParams): LeadDateRangePresetKey => {
  const modern = params.get('daterange');
  if (modern) return parsePresetKey(modern);
  const legacy = params.get('range');
  if (legacy && legacy in LEGACY_RANGE_MIGRATION) return LEGACY_RANGE_MIGRATION[legacy]!;
  return DEFAULT_ASIDE_RANGE;
};

// `ALL_TYPES` sentinel removed 2026-06-25 with the Type dropdown.

const BUCKET_BANNER: Record<LeadBucket, { label: string; tint: string }> = {
  OPEN: { label: 'Active pipeline', tint: 'bg-blue-50 text-blue-700 border-blue-200' },
  WON: { label: 'Won', tint: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  LOST: { label: 'Lost', tint: 'bg-red-50 text-red-700 border-red-200' },
};

// IST month helpers. The "active this month" lens defaults to the
// current IST calendar month so the operations team's day boundaries
// match the server-side filter (which also uses Asia/Kolkata - see
// computeIstMonthBoundsUtc in leads.service.ts). Fixed +05:30 offset,
// no DST, so a plain offset shift is correct year-round.
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const currentIstMonthKey = (): string => {
  const nowIst = new Date(Date.now() + IST_OFFSET_MS);
  const yyyy = nowIst.getUTCFullYear();
  const mm = String(nowIst.getUTCMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}`;
};
const parseMonthKey = (key: string): { year: number; month: number } | null => {
  const m = key.match(/^(\d{4})-(0[1-9]|1[0-2])$/);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) };
};
const shiftMonth = (key: string, delta: number): string => {
  const parsed = parseMonthKey(key) ?? parseMonthKey(currentIstMonthKey())!;
  const total = parsed.year * 12 + (parsed.month - 1) + delta;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
};
const formatMonth = (key: string): string => {
  const parsed = parseMonthKey(key);
  if (!parsed) return key;
  return `${MONTH_NAMES[parsed.month - 1] ?? ''} ${parsed.year}`;
};
const formatShortMonth = (key: string): string => {
  const parsed = parseMonthKey(key);
  if (!parsed) return key;
  return `${(MONTH_NAMES[parsed.month - 1] ?? '').slice(0, 3)} ${parsed.year}`;
};
/** UTC instant of `YYYY-MM-01 00:00 IST` - matches the server boundary. */
const istMonthStartUtcMs = (key: string): number => {
  const parsed = parseMonthKey(key);
  if (!parsed) return 0;
  return Date.UTC(parsed.year, parsed.month - 1, 1) - IST_OFFSET_MS;
};

/**
 * Left aside of the two-pane Lead detail screen.
 *
 * Filter state is **URL-driven** so the pipeline tiles (and any future entry
 * point) can deep-link the operator directly into a filtered list. On mount,
 * the aside reads:
 *
 *   - `range`  - date range key (default '30')
 *   - `type`   - query_type UUID
 *   - `status` - query_status UUID (single-stage filter from a stage tile)
 *   - `bucket` - bucket-level filter (OPEN/WON/LOST) from a KPI tile
 *
 * Whatever the user changes in the aside writes back to the URL (via `replace`),
 * so the back button works and the lead detail's deep link survives a reload.
 * Search term is intentionally NOT URL-synced - operators rarely share search
 * states, and per-keystroke URL writes add noise.
 *
 * When the URL carries a bucket or status filter, a small "Filtered by X"
 * banner sits between the chip row and the list with a clear (×) affordance.
 */
export const LeadInboxAside = ({
  selectedLeadId,
  onSelect,
}: LeadInboxAsideProps): React.JSX.Element => {
  const [params, setParams] = useSearchParams();
  // Every write below merges into the LIVE URL. The aside shares its route
  // with a nuqs-backed leads grid, and nuqs writes straight to History without
  // telling React Router - rebuilding the query string from `params` would
  // silently drop the grid's page / rows-per-page / `f.*` filters.
  const writeParams = useUrlParamWriter();
  const me = useCurrentUser();
  // Same policy as LeadInboxPage date-range picker: only ADMIN may change
  // relative createdAt windows. Non-admins stay on the default and cannot
  // override via `?range=`.
  const roleReady = me !== null;
  // 2026-08-01. Date picker removed for EVERYONE - the lead-detail aside mirrors
  // the inbox, which is now a fixed this-month + carry-forward snapshot (historical
  // filtering lives in Reports). Always false -> the lock effect + effectiveRange
  // pin the aside to the this-month default. Drill-in flags (f.combined /
  // f.activeCarryover / stage / lens) are unaffected.
  const canChangeAsideRange = false;

  // 2026-07-21. Derived from URL (no useState). `DateRangeFilter` now
  // owns the write path for the date range - the aside just observes
  // the URL param and mirrors it into the data query. Removing the
  // duplicate state prevents drift between the picker and the query.
  const range: LeadDateRangePresetKey = parseRange(params);
  // Effective range used for queries + URL writes. Non-admins are locked
  // even before the URL scrub lands.
  const rangeLocked = roleReady && !canChangeAsideRange;
  const effectiveRange = rangeLocked ? DEFAULT_ASIDE_RANGE : range;
  // 2026-06-25: `queryTypeId` state + Type dropdown were removed
  // per ops feedback - the filter was rarely used and the Tile
  // dropdown below now occupies the slot.
  // `stage` is the modern URL key (used by the tracker tiles, the
  // leads-list page, every per-status drill-in). `status` is a
  // legacy fallback so old bookmarks keep working - this branch
  // disappears once we are confident no live URL still carries it.
  // One chip at a time on mount: Revert Pending (or other tile queues)
  // win over status/sub-status so Revert Pending + Proposal never both
  // light up from a stale deep-link.
  const initialRevertPending = params.get('revertPending') === 'true';
  const initialRejectedApproval = params.get('rejectedApproval') === 'true';
  const initialOtherTile =
    params.get('largeLeadQuery') === 'true' ||
    params.get('requiresFirstContact') === 'true' ||
    params.get('freshToday') === 'true' ||
    params.get('dailyTaskPool') === 'true' ||
    Boolean(params.get('nextFollowUpFrom') || params.get('nextFollowUpTo'));
  const initialChipOccupied = initialRevertPending || initialRejectedApproval || initialOtherTile;
  // Sub-status wins over parent status when both are present.
  const initialSubStatusId = initialChipOccupied
    ? null
    : (params.get('substage') ?? params.get('substatus'));
  const [statusId, setStatusId] = useState<string | null>(() =>
    initialChipOccupied || initialSubStatusId
      ? null
      : (params.get('stage') ?? params.get('status')),
  );
  // 2026-06-25: sub-status drill-in (Proposal / Hot / Warm under
  // Active). Mutually exclusive with `statusId` - picking a
  // sub-status clears the parent status filter and vice versa.
  // `substage` is the canonical key; accept legacy `substatus` so older
  // KPI-strip deep-links still pre-select Proposal / Hot / Warm.
  const [subStatusId, setSubStatusId] = useState<string | null>(() => initialSubStatusId);
  const [bucket, setBucket] = useState<LeadBucket | null>(() => {
    const raw = params.get('bucket');
    return raw === 'OPEN' || raw === 'WON' || raw === 'LOST' ? raw : null;
  });
  // Tile-driven filter mirror. Each is seeded from the URL on mount
  // so a tracker tile click pre-narrows the queue to just the
  // matching leads; the operator can clear any of them from the
  // banner row below. Every value is also kept in sync with the URL
  // (effect below) so the back button steps over filter edits
  // exactly the way `bucket` / `statusId` already do.
  const [revertPending, setRevertPending] = useState<boolean>(() => initialRevertPending);
  const [rejectedApproval, setRejectedApproval] = useState<boolean>(() => initialRejectedApproval);
  const [largeLeadQuery, setLargeLeadQuery] = useState<boolean>(
    () => params.get('largeLeadQuery') === 'true',
  );
  const [nextFollowUpFrom, setNextFollowUpFrom] = useState<string | null>(() =>
    params.get('nextFollowUpFrom'),
  );
  const [nextFollowUpTo, setNextFollowUpTo] = useState<string | null>(() =>
    params.get('nextFollowUpTo'),
  );
  const [requiresFirstContact, setRequiresFirstContact] = useState<boolean>(
    () => params.get('requiresFirstContact') === 'true',
  );
  // Daily Tracker "Today's Fresh Leads" drill-in. Narrows the list to
  // created-today without rewriting inbox `daterange`/`from`/`to`, so
  // Back → Lead inbox keeps This month (or whatever the inbox had).
  const [freshToday, setFreshToday] = useState<boolean>(() => params.get('freshToday') === 'true');
  // Daily Tracker "Total Tasks" drill-in. Narrows the list to the server-side
  // `dailyTaskPool` union (fresh-today OR due-today OR missed OR revert-pending
  // OR not-qualified), deduped. The server computes "today" internally, so this
  // carries NO date window and suppresses the month lens (whole-pool count).
  const [dailyTaskPool, setDailyTaskPool] = useState<boolean>(
    () => params.get('dailyTaskPool') === 'true',
  );
  // "Active this month" lens. Default ON, pinned to the current IST
  // calendar month. Carryover is OFF by default (2026-07-10 flip -
  // operators asked for a clean current-month view, opting into
  // last month's leftovers explicitly when needed). Operator can:
  //   - navigate to a different month via the prev/next chevrons
  //   - toggle carryover on (surface last month's still-OPEN
  //     active leads) - state syncs to URL as `carry=1`
  //   - dismiss the lens entirely (falls back to the existing
  //     range-based behaviour) by clicking the lens pill's X
  // All three pieces are URL-synced so deep-links survive a reload.
  const [activeMonth, setActiveMonth] = useState<string>(
    () => params.get('month') || currentIstMonthKey(),
  );
  const [includeCarryover, setIncludeCarryover] = useState<boolean>(
    () => params.get('carry') === '1',
  );
  const [lensEnabled, setLensEnabled] = useState<boolean>(() => params.get('lens') !== '0');
  // Owner (assigned agent) scope. Shares the `?ownerId=` param with the Lead
  // Inbox owner picker, so navigating inbox → detail carries the selection.
  // Empty = "All owners". The backend enforces role-hierarchy scoping.
  const [ownerId, setOwnerId] = useState<string | null>(() => params.get('ownerId'));
  const [searchTerm, setSearchTerm] = useState('');
  // Lead Intent (priority) filter. `ANY` = no filter (default). Narrows the
  // main list, the Total count, and the stage-chip badges alike. Hydrated from
  // the URL so the "High Intent" tile drill-in (?priority=HIGH) lands with the
  // dropdown pre-set, and the pick round-trips on shared links / Back.
  const [intentFilter, setIntentFilter] = useState<string>(() => {
    const p = (params.get('priority') ?? '').toUpperCase();
    return p === 'URGENT' || p === 'HIGH' || p === 'NORMAL' || p === 'LOW' ? p : 'ANY';
  });
  // Destination scope - same GLOBAL narrowing as intent (list, Total, chip
  // badges), so "owner Ashutosh + Kerala" lists exactly that owner's Kerala
  // leads. Same `destinationId` match as the Lead Inbox grid's Destination
  // chip, so both read the same counts. URL-synced (`?destination=`).
  const [destinationFilter, setDestinationFilter] = useState<string>(
    () => params.get('destination') ?? '',
  );
  const destinations = useDestinationOptionsForLeads();

  const statuses = useQueryStatusOptionsWithBucket();
  // 2026-06-25: Active sub-statuses (Proposal / Hot / Warm) for
  // the additional pill row. Hook is always called; data falls
  // through to [] until the parent Active row resolves.
  const activeStatusForSubs = useMemo(
    () => statuses.data.find((s) => s.code === 'active') ?? null,
    [statuses.data],
  );
  const activeSubStatuses = useQuerySubStatusOptionsForParent(activeStatusForSubs?.id ?? null);
  const proposalSub = useMemo(
    () => activeSubStatuses.data.find((s) => s.code === 'proposal') ?? null,
    [activeSubStatuses.data],
  );
  const hotSub = useMemo(
    () => activeSubStatuses.data.find((s) => s.code === 'hot') ?? null,
    [activeSubStatuses.data],
  );
  const warmSub = useMemo(
    () => activeSubStatuses.data.find((s) => s.code === 'warm') ?? null,
    [activeSubStatuses.data],
  );
  // Long Follow-up has its own inbox tab; hide it from the detail aside
  // pills and exclude it from every list/count query here.
  const longFollowupStatusId = useMemo(
    () => statuses.data.find((s) => s.code === 'long_followup')?.id,
    [statuses.data],
  );
  // Resolved so the Revert Pending filter can exclude Not Qualified leads -
  // matching the Daily Tracker / KPI-strip definition so counts line up.
  const notQualifiedStatusId = useMemo(
    () => statuses.data.find((s) => s.code === 'not_qualified')?.id,
    [statuses.data],
  );
  const parentStatusPills = useMemo(
    () => statuses.data.filter((s) => s.code !== 'long_followup'),
    [statuses.data],
  );

  // Stale deep-links with ?stage=long_followup should not leave the
  // aside filtering to an empty slice - clear back to All.
  useEffect(() => {
    if (!longFollowupStatusId || statusId !== longFollowupStatusId) return;
    setStatusId(null);
  }, [longFollowupStatusId, statusId]);

  // Non-admin lock: force the date range back to the aside default and
  // wipe any explicit custom bounds. Mirrors the inbox header lock so
  // a `?daterange=` (or legacy `?range=`) in a shared URL cannot
  // bypass the hidden picker.
  //
  // 2026-07-21. The URL is now REWRITTEN to `?daterange=<DEFAULT>`
  // rather than emptied. Emptying collapsed `hasExplicitDateContext`
  // to false, which flipped the lens auto-suppressor off and let the
  // active-this-month lens re-activate - producing counts that don't
  // match the inbox KPI (which uses `createdAt`) for non-admin users
  // who have no way to toggle either off. This ran on both non-default
  // URLs AND bare URLs (previously skipped): both now converge to the
  // aside default, so the aside behaves exactly like the same-preset
  // inbox load for every entry path.
  // 2026-08-01. Date params are never stored - the aside is a fixed this-month
  // + carry-forward snapshot. Strip any that arrive from a drill-in or stale
  // link (daterange/from/to/range + the now-defunct f.activeCarryover carryover
  // flag, which is strict-on in code). Keeps the detail URL to the lead id +
  // drill flags (f.combined / stage / lens) + ownerId.
  useEffect(() => {
    if (!roleReady || canChangeAsideRange) return;
    const next = new URLSearchParams(params);
    next.delete('daterange');
    next.delete('range');
    next.delete('from');
    next.delete('to');
    next.delete('f.activeCarryover');
    next.delete('activeCarryover');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleReady, canChangeAsideRange, params]);

  // Sync filter state → URL search params. `replace: true` keeps history
  // clean - the back button steps over filter tweaks rather than every
  // intermediate combination.
  useEffect(() => {
    const next = new URLSearchParams(params);
    // 2026-07-21. `DateRangeFilter` (the calendar picker at the top of
    // the aside) owns the `?daterange=`, `?from=`, `?to=` writes now.
    // The aside no longer echoes those params here - doing so would
    // fight the picker's own writes on rapid state changes.
    // Legacy `?range=` param is proactively dropped so migrated URLs
    // stop carrying the deprecated key.
    next.delete('range');
    // `type` URL param is dropped 2026-06-25 with the Type dropdown.
    next.delete('type');
    // 2026-08-01. Date params + the carryover flag are never stored (fixed
    // this-month + carry-forward snapshot, carryover strict-on in code) - drop
    // them here too so a drill-in that forwards them can't leave them behind.
    next.delete('daterange');
    next.delete('from');
    next.delete('to');
    next.delete('f.activeCarryover');
    next.delete('activeCarryover');
    // Normalise on the modern `stage` key. Drop the legacy `status`
    // key on first write so the URL stays canonical.
    if (statusId) next.set('stage', statusId);
    else next.delete('stage');
    if (subStatusId) next.set('substage', subStatusId);
    else next.delete('substage');
    next.delete('status');
    if (bucket) next.set('bucket', bucket);
    else next.delete('bucket');
    // Tile-driven mirrors. `true`/value writes, otherwise delete -
    // never write `false`/empty (would clutter the URL and read as
    // "explicitly off" on the next mount).
    if (revertPending) next.set('revertPending', 'true');
    else next.delete('revertPending');
    if (rejectedApproval) next.set('rejectedApproval', 'true');
    else next.delete('rejectedApproval');
    if (largeLeadQuery) next.set('largeLeadQuery', 'true');
    else next.delete('largeLeadQuery');
    if (nextFollowUpFrom) next.set('nextFollowUpFrom', nextFollowUpFrom);
    else next.delete('nextFollowUpFrom');
    if (nextFollowUpTo) next.set('nextFollowUpTo', nextFollowUpTo);
    else next.delete('nextFollowUpTo');
    if (requiresFirstContact) next.set('requiresFirstContact', 'true');
    else next.delete('requiresFirstContact');
    if (freshToday) next.set('freshToday', 'true');
    else next.delete('freshToday');
    if (dailyTaskPool) next.set('dailyTaskPool', 'true');
    else next.delete('dailyTaskPool');
    // Lens state. Only write non-default values - keeps the URL clean
    // for the common case (lens on, current month, carryover off).
    if (lensEnabled && activeMonth !== currentIstMonthKey()) next.set('month', activeMonth);
    else next.delete('month');
    // 2026-07-10. Carryover default flipped to OFF - opt-in via `carry=1`.
    if (lensEnabled && includeCarryover) next.set('carry', '1');
    else next.delete('carry');
    if (!lensEnabled) next.set('lens', '0');
    else next.delete('lens');
    if (ownerId) next.set('ownerId', ownerId);
    else next.delete('ownerId');
    if (intentFilter !== 'ANY') next.set('priority', intentFilter);
    else next.delete('priority');
    if (destinationFilter) next.set('destination', destinationFilter);
    else next.delete('destination');
    // Only write if there's an actual change - avoids a redundant render loop
    // when the URL is already in sync.
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
    // params + setParams are stable references from useSearchParams; we
    // intentionally only react to filter-state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveRange,
    statusId,
    subStatusId,
    bucket,
    revertPending,
    rejectedApproval,
    largeLeadQuery,
    nextFollowUpFrom,
    nextFollowUpTo,
    requiresFirstContact,
    freshToday,
    dailyTaskPool,
    activeMonth,
    includeCarryover,
    lensEnabled,
    ownerId,
    intentFilter,
    destinationFilter,
  ]);

  // 2026-07-21. Unified date-range vocabulary. The aside owns ONE
  // `range` preset (a `LeadDateRangePresetKey`) hydrated from the same
  // `?daterange=` URL param the inbox writes, plus a legacy `?range=`
  // fallback for old bookmarks (see `parseRange`). Custom bounds are
  // supplied via `?from=` + `?to=` (as before), interpreted by the
  // shared `resolveLeadDateRange` helper - which returns concrete
  // dates for named presets and reads the URL bounds only when
  // `preset === 'custom'`.
  //
  // The `useInboxBounds` / `inboxFrom` / `inboxTo` split was retired
  // because there is no longer a "separate inbox context" - the
  // aside's own `range` state IS what the inbox forwarded (both
  // rooted in `?daterange=`).
  const urlFromRaw = params.get('from');
  const urlToRaw = params.get('to');
  const asideDateBounds = resolveLeadDateRange(effectiveRange, urlFromRaw, urlToRaw);
  const asideFrom = isoDate(asideDateBounds.from);
  const asideTo = isoDate(asideDateBounds.to);
  const isAllTimeRange = effectiveRange === 'all';

  // `hasExplicitDateContext` preserves the pre-unification semantic
  // used by the auto-lens suppressor below: the operator (or an inbox
  // drill-in) has narrowed to a specific window, so the "active this
  // month" lens should stay off until they explicitly re-enable it.
  const hasExplicitDateContext =
    params.has('daterange') || params.has('range') || Boolean(urlFromRaw || urlToRaw);
  // Report-tile drill (2026-10-01): an explicit created-window that BYPASSES the
  // always-on combined pool below, so a Reports Total/Current/Carry tile
  // reproduces its exact month cohort - for ANY month. The window travels on
  // `rptFrom`/`rptTo` (NOT `from`/`to`, which the lock effect strips), plus
  // optional folds: `openAsOf` (Carry = open as of the 1st), `rptCarry` (fold
  // still-open carry backlog, Total), `rptClosed` (fold this-period closures,
  // Total pool). The chip badges honour the same window (see StatusCount).
  const rptExplicit = params.get('rptExplicit') === '1';
  const rptOpenAsOf = params.get('openAsOf') || undefined;
  const rptFrom = params.get('rptFrom') || undefined;
  const rptTo = params.get('rptTo') || undefined;
  const rptCarry = params.get('rptCarry') === '1';
  const rptClosed = params.get('rptClosed') === '1';
  // Itinerary-coverage drill (report "Active w/ Itinerary"): none | in_progress | sent.
  const itineraryStateRaw = params.get('itineraryState');
  const itineraryState =
    itineraryStateRaw === 'none' ||
    itineraryStateRaw === 'in_progress' ||
    itineraryStateRaw === 'sent' ||
    itineraryStateRaw === 'unpublished'
      ? itineraryStateRaw
      : undefined;
  const carryforwardOnly =
    params.get('f.carryforward') === 'yes' || params.get('carryforward') === 'yes';
  // 2026-08-04. Combined is now the FIXED, always-on behavior - not a URL flag.
  // The Lead Inbox list + every chip badge always shows the whole working pool
  // (this-month + carry-forward + long-followup), so a drilled-in number always
  // equals its Daily Tracker tile. There is no date picker and no `f.combined`
  // param: the pool is defined in code, and only real filters (owner, status,
  // follow-up, search) narrow it. Hardcoded `true` so nothing can defeat it and
  // the numbers never depend on the URL again.
  const combinedWindow = true;
  // 2026-07-27. "Total Leads" tracker drill-in. Folds the still-OPEN
  // carry-forward backlog into the selected (this-month) window - the same
  // `includeActiveCarryover` predicate the Pipeline grid uses - so the
  // aside's Total matches the Total Leads tile (This month + Carry forward),
  // which a plain date window or the stage-entered `activeMonth` lens can't
  // reproduce. Floored at the cutover anchor like every other CF surface.
  // A flag (not from/to bounds), so it survives the non-admin date lock.
  const activeCarryoverDrill =
    params.get('f.activeCarryover') === 'yes' || params.get('activeCarryover') === 'yes';

  // "Unassigned" and "Long Follow-up" are two extra options in the aside's
  // filter dropdown (alongside All leads / Today's follow-ups / Revert pending
  // etc.). Both are URL-flag lenses so they round-trip on shared links / Back:
  //   - Unassigned    (f.unassigned=yes)  -> no-agent leads (server resolves who
  //                    counts per role: admin=all, SALES_LEAD=their team's).
  //   - Long Follow-up (f.longFollowup=yes) -> the parked long_followup stage,
  //                    which is otherwise hidden from the aside chips + queries.
  const unassignedOnly = params.get('f.unassigned') === 'yes';
  const longFollowupOnly = params.get('f.longFollowup') === 'yes';

  // Lens auto-suppress: when the operator has narrowed via something
  // that the lens would override or contradict, switch the lens OFF
  // for this query so the operator gets exactly what they clicked.
  //   - explicit from/to / daterange forwarded from the inbox
  //   - bucket other than OPEN (lens forces OPEN)
  //   - a status chip other than `active` (we don't know the code on
  //     the master here, so any explicit status chip suppresses)
  //   - revertPending / nextFollowUp / requiresFirstContact tile drilldowns
  // The state `lensEnabled` still controls the visible toggle; this
  // derived flag is what actually goes into the query.
  // Suppression terms independent of the status / sub-status selection. The
  // per-chip count builder re-adds its own status term on top of this so each
  // chip can decide its own lens state.
  const lensSuppressedBase =
    hasExplicitDateContext ||
    carryforwardOnly ||
    combinedWindow ||
    activeCarryoverDrill ||
    (bucket !== null && bucket !== 'OPEN') ||
    revertPending ||
    rejectedApproval ||
    largeLeadQuery ||
    Boolean(nextFollowUpFrom || nextFollowUpTo) ||
    requiresFirstContact ||
    freshToday ||
    dailyTaskPool ||
    unassignedOnly ||
    longFollowupOnly;
  const lensSuppressedByOverride = lensSuppressedBase || statusId !== null || subStatusId !== null;
  const lensActive = lensEnabled && !lensSuppressedByOverride;
  // Month lens eligibility for chip-count badges only - independent of the
  // currently selected status chip so bracket numbers stay in the June 2026
  // context while the lens strip is visible.
  const chipCountLensEligible = lensEnabled && !lensSuppressedBase;

  // Single source of truth for building a lead-list query from the current
  // filter state. Takes an EXPLICIT status / sub-status selection (rather than
  // reading the aside's own state) so the per-chip count badges can ask "what
  // would I see if THIS chip were the active filter?" - including re-deciding
  // whether the active-this-month lens is suppressed (selecting any status /
  // sub-status suppresses it for the main list, mirroring the click-through).
  // When the month lens strip is visible and `forChipCount` is true, chip
  // badges keep the lens and slice by status so bracket numbers match the
  // June 2026 context instead of falling back to the 30-day range.
  const buildLeadQuery = useCallback(
    (
      sel: { statusId: string | null; subStatusId: string | null },
      opts?: { forChipCount?: boolean },
    ): LeadListQuery => {
      const forChip = opts?.forChipCount === true;
      // Chip badges keep the month lens whenever the lens strip is eligible,
      // even if a status chip is selected (that selection only suppresses the
      // lens for the main list). Using `lensActive` here would collapse every
      // bracket to the 30-day fallback while a chip is selected.
      const suppressLensForSelection = Boolean(sel.statusId) || Boolean(sel.subStatusId);
      const suppressed =
        lensSuppressedBase || (suppressLensForSelection && !(forChip && chipCountLensEligible));
      const effLensActive = lensEnabled && !suppressed;
      const base: LeadListQuery = {
        page: 1,
        pageSize: 50,
        sortDir: 'desc',
        excludeWebsite: true,
        // Fold the unassigned (exit-released) pool into every aside count so the
        // brackets + Total match the Daily Tracker / KPI tiles (which count with
        // includeUnassignedPool). RBAC still fences non-admins server-side, so
        // this only surfaces unassigned rows for unscoped (ADMIN/OPS) roles.
        includeUnassignedPool: true,
      };
      // Lead Intent (priority) scope - a GLOBAL narrowing applied to the main
      // list, the Total count, AND the chip badges so every number reflects
      // the picked intent. `ANY` = no filter.
      if (intentFilter !== 'ANY') base.priority = intentFilter as LeadPriority;
      if (destinationFilter) base.destinationId = destinationFilter;
      // status + sub-status are mutually exclusive - a sub-status (Proposal /
      // Hot / Warm) drills under Active without needing the parent filter.
      if (sel.subStatusId) {
        base.currentSubStatusId = sel.subStatusId;
        // A sub-stage is always read as "Active AND this sub-stage", never
        // as a free-floating tag. Pinning the parent stage + OPEN bucket
        // keeps a lead that moved on (or closed) while keeping a leftover
        // sub-status tag - migrated rows do this - out of the badge. It is
        // also what makes Proposal + Hot + Warm add up to Active on the
        // All-leads KPI strip, which counts them the same way.
        // Guarded to Active's own sub-statuses so a deep-link to some other
        // parent's sub-stage isn't silently zeroed out.
        if (activeStatusForSubs && activeSubStatuses.data.some((s) => s.id === sel.subStatusId)) {
          base.currentStatusId = activeStatusForSubs.id;
        }
        base.currentBucket = 'OPEN';
      } else if (sel.statusId) {
        base.currentStatusId = sel.statusId;
        // Pin the stage's own bucket. Two reasons:
        //   - ADR-0058: terminal stages (Won / Lost) need the bucket for the
        //     server's date reroute to fire, so the window applies to
        //     `closedAt` not `createdAt`. Without it, Won chip + "This month"
        //     counts leads *created* in July that are now Won - divergent
        //     from the inbox KPI ("Won this month" = converted in July).
        //   - OPEN stages need it for the same reason the tracker pins it:
        //     a row whose `currentBucket` drifted from its stage would count
        //     in the chip badge but not in the list below (the list carries
        //     the URL's `bucket=OPEN`), so the Active chip read 185 while
        //     Total read 184 for the identical slice.
        const selectedPill = parentStatusPills.find((s) => s.id === sel.statusId);
        const pillBucket = (selectedPill?.bucket ?? null) as LeadBucket | null;
        if (pillBucket) base.currentBucket = pillBucket;
      }
      // Long Follow-up dropdown lens: pin the parked stage on the MAIN list only
      // (it isn't a chip, so it never arrives via sel.statusId). Not applied to
      // chip counts - each bracket sets its own stage, same as every other lens.
      if (!forChip && longFollowupOnly && longFollowupStatusId) {
        base.currentStatusId = longFollowupStatusId;
        base.currentBucket = 'OPEN';
      }
      // Bracket counts show each chip's population under the shared date /
      // owner scope only - not intersected with the currently active tile or
      // bucket filter (otherwise every chip mirrors the selected slice).
      if (!forChip) {
        if (bucket) base.currentBucket = bucket;
        // Tile-driven narrowing - the server just gets a more selective WHERE.
        if (revertPending) base.revertPending = true;
        if (rejectedApproval) base.rejectedApproval = true;
        if (largeLeadQuery) base.largeLeadQuery = true;
        if (nextFollowUpFrom) base.nextFollowUpFrom = nextFollowUpFrom;
        if (nextFollowUpTo) base.nextFollowUpTo = nextFollowUpTo;
        if (requiresFirstContact) base.requiresFirstContact = true;
        if (dailyTaskPool) base.dailyTaskPool = true;
        if (itineraryState) base.itineraryState = itineraryState;
      }
      // Unassigned toggle wins over the owner filter (they contradict: an owner
      // has leads, unassigned has none). Otherwise scope to the picked owner.
      if (unassignedOnly) {
        base.assignedAgentIsNull = true;
      } else if (ownerId) {
        base.assignedAgentId = ownerId;
      }
      // "All leads" = nothing else selected = the date-scoped Total pool.
      const isPlainAllView =
        !sel.statusId &&
        !sel.subStatusId &&
        !revertPending &&
        !rejectedApproval &&
        !largeLeadQuery &&
        !nextFollowUpFrom &&
        !nextFollowUpTo &&
        !requiresFirstContact &&
        !freshToday &&
        !dailyTaskPool &&
        !unassignedOnly &&
        !longFollowupOnly &&
        !carryforwardOnly &&
        !bucket;
      // Tile-parity exclude for Not Qualified - Revert Pending ONLY: NQ leads
      // are brand-new / never contacted, so they cannot be "awaiting a revert".
      // (Follow-up tiles no longer exclude NQ - many NQ leads carry follow-ups.)
      const excludeNqForTileParity =
        !forChip && Boolean(revertPending) && Boolean(notQualifiedStatusId);
      if (excludeNqForTileParity) {
        base.excludeStatusId = notQualifiedStatusId;
      } else if (
        longFollowupStatusId &&
        !longFollowupOnly &&
        !isPlainAllView &&
        !combinedWindow &&
        !dailyTaskPool
      ) {
        // Parked long_followup is kept out of NARROWED stage/lens views, but is
        // ALWAYS part of the whole-pool "All leads" view (isPlainAllView) and the
        // combined all-time drills - long-followup is part of the pool (the
        // decision), so it's included by default there, no URL flag needed.
        base.excludeStatusId = longFollowupStatusId;
      }
      if (rptExplicit) {
        // Reports drill (Total/Current/Carry tile): the LITERAL created-window
        // from `rptFrom`/`rptTo` (survives the date-param strip), plus optional
        // folds. Reproduces the exact cohort the tile counted, for ANY month,
        // and applies to chip badges too so their numbers match the list.
        if (rptFrom) base.createdAtFrom = rptFrom;
        if (rptTo) base.createdAtTo = rptTo;
        if (rptOpenAsOf) base.openAsOf = rptOpenAsOf; // Carry: open as of the 1st
        if (rptCarry) {
          // Total: fold the still-open carried backlog into the window.
          base.includeActiveCarryover = true;
          base.activeCarryoverFrom = CARRYFORWARD_FLOOR_ISO;
        }
        if (rptClosed) base.includeClosedThisPeriod = true; // Total pool: + closures
      } else if (!forChip && isPlainAllView && !isAllTimeRange && (asideFrom || asideTo)) {
        // The Total pool - ALWAYS this-month window + carry-forward + this-period
        // closures (+ long-followup, included via the exclusion above). No URL
        // flag: carry-forward is strict-on so the count can't be defeated.
        if (asideFrom) base.createdAtFrom = asideFrom;
        if (asideTo) base.createdAtTo = asideTo;
        base.includeActiveCarryover = true;
        base.activeCarryoverFrom = CARRYFORWARD_FLOOR_ISO;
        base.includeClosedThisPeriod = true;
      } else if (carryforwardOnly) {
        // Strict carry-forward slice from the tracker strip
        // (`createdAt < currentMonthStart`, bucket=OPEN). Matches
        // `LeadsListPage.toQuery` and `LeadsTrackerStrip` counts.
        // Floored at the cutover anchor so the pre-migration legacy
        // backlog never lands in carry-forward.
        base.currentBucket = 'OPEN';
        base.createdAtFrom = CARRYFORWARD_FLOOR_ISO;
        base.createdAtTo = getPrevMonthEndIso();
      } else if (!forChip && freshToday) {
        // Daily Tracker "Today's Fresh Leads" - pin the list to today while
        // leaving inbox `daterange`/`from`/`to` untouched for Back → inbox.
        // IST-anchored so this stays consistent with the tile that opened
        // the drilldown even when the browser is not IST.
        //
        // 2026-07-26. MUST be checked before `combinedWindow`: the Fresh
        // Today tile drills in with BOTH `freshToday=true` AND
        // `f.combined=yes` (the combined strip always forwards the combined
        // flag). Fresh Today is a today-only slice, not an all-time one, so
        // its today window has to win - otherwise combinedWindow drops all
        // date bounds and the list shows the whole pool (63k) instead of
        // today's handful.
        const todayIso = istIsoDate();
        base.createdAtFrom = todayIso;
        base.createdAtTo = todayIso;
      } else if (combinedWindow) {
        // Combined working pool - the fixed default. FLOOR the created window at
        // the carry-forward cutover (no upper bound) so the count matches the
        // tile's floored carry number EXACTLY: without the floor one pre-cutover
        // OPEN lead leaks in (e.g. Active 316 vs the tile's 315), and the chips
        // would sum to 1,340 instead of the plain-all Total of 1,339. Terminal
        // Won/Lost pills are left unbounded here so the terminal anchor below
        // pins them to THIS month (they are period-outcome counts, not a pool).
        if (base.currentBucket !== 'WON' && base.currentBucket !== 'LOST') {
          base.createdAtFrom = CARRYFORWARD_FLOOR_ISO;
        }
      } else if (effLensActive) {
        // Month lens wins over the relative date range: the server's
        // `activeMonth` clause already pins `stage_entered_at` to the chosen
        // IST month, so layering a `createdAt` window on top would only ever
        // narrow it further - confusing for the operator.
        base.activeMonth = activeMonth;
        if (includeCarryover) base.includeCarryover = true;
      } else if (!dailyTaskPool && !isAllTimeRange && (asideFrom || asideTo)) {
        // 2026-07-21. Unified date range - one path for both the
        // aside's own picker and any inbox-forwarded preset (they
        // share the `?daterange=` URL param). `resolveLeadDateRange`
        // handles all preset -> {from,to} math including the
        // `custom` preset (which reads the URL `from`/`to` bounds).
        if (asideFrom) base.createdAtFrom = asideFrom;
        if (asideTo) base.createdAtTo = asideTo;
        // Carry-forward is ALWAYS folded in for OPEN-ish (non-terminal) stage
        // views - fold still-OPEN pre-window leads into the in-window rows so an
        // Active/NQ view shows "this month + carry-forward", matching its tile.
        // No URL flag (f.activeCarryover is gone) - it's strict-on. Terminal
        // Won/Lost views stay strictly this-period (their OPEN carry leg is empty
        // anyway). Applies to chip badges too - the fold-in is part of the window.
        if (base.currentBucket !== 'WON' && base.currentBucket !== 'LOST') {
          base.includeActiveCarryover = true;
          base.activeCarryoverFrom = CARRYFORWARD_FLOOR_ISO;
        }
      }
      // Won / Lost are period-outcome counts (closed IN a period), not a standing
      // pool - the Daily Tracker Won/Lost tiles are always "This month". A combined
      // / all-time drill (f.combined=yes, or daterange=all) leaves the OPEN pills
      // unbounded, which would make a terminal-bucket pill show the ALL-TIME closed
      // total (e.g. Lost 2,315) instead of what closed this month (0). So when a
      // WON/LOST pill has no date window, anchor it to the current IST month; the
      // server reroutes createdAt->closedAt for terminal buckets (ADR-0058).
      const isTerminalPill = base.currentBucket === 'WON' || base.currentBucket === 'LOST';
      if (isTerminalPill && !base.createdAtFrom && !base.createdAtTo) {
        const [anchorYear, anchorMonth] = istIsoDate().split('-');
        base.createdAtFrom = `${anchorYear}-${anchorMonth}-01`;
        base.createdAtTo = istIsoDate();
      }
      // Won / Lost are ASSIGNED-ONLY everywhere (the Daily Tracker tiles, the
      // All-leads cards and here), so the aside list, Total and chip badges all
      // agree with the tile. Unassigned closed leads are out of scope; the
      // Unassigned filter still surfaces them explicitly when picked.
      if (isTerminalPill && !unassignedOnly && !ownerId) {
        base.assignedAgentIsNotNull = true;
        base.includeUnassignedPool = undefined;
      }
      const trimmed = searchTerm.trim();
      if (trimmed.length >= 3) base.q = trimmed;
      return base;
    },
    [
      lensSuppressedBase,
      lensEnabled,
      bucket,
      revertPending,
      rejectedApproval,
      largeLeadQuery,
      nextFollowUpFrom,
      nextFollowUpTo,
      requiresFirstContact,
      freshToday,
      dailyTaskPool,
      rptExplicit,
      rptOpenAsOf,
      rptFrom,
      rptTo,
      rptCarry,
      rptClosed,
      itineraryState,
      activeMonth,
      includeCarryover,
      hasExplicitDateContext,
      isAllTimeRange,
      asideFrom,
      asideTo,
      effectiveRange,
      searchTerm,
      longFollowupStatusId,
      notQualifiedStatusId,
      chipCountLensEligible,
      carryforwardOnly,
      combinedWindow,
      activeCarryoverDrill,
      unassignedOnly,
      longFollowupOnly,
      longFollowupStatusId,
      ownerId,
      intentFilter,
      destinationFilter,
      parentStatusPills,
      activeStatusForSubs,
      activeSubStatuses.data,
    ],
  );

  // The main list reflects the aside's current status / sub-status selection.
  const baseQuery = useMemo(
    () => buildLeadQuery({ statusId, subStatusId }),
    [buildLeadQuery, statusId, subStatusId],
  );
  // "Load more" pagination (see LIST_PAGE_STEP note). Starts at one
  // step and grows per click; snaps back to the first step whenever
  // the filter context changes so a deep-grown list doesn't leak into
  // a different slice.
  const [listPageSize, setListPageSize] = useState<number>(LIST_PAGE_STEP);
  useEffect(() => {
    setListPageSize(LIST_PAGE_STEP);
  }, [baseQuery]);
  const query = useMemo(
    () => ({ ...baseQuery, pageSize: listPageSize }),
    [baseQuery, listPageSize],
  );

  // keepPreviousData: "Load more" grows the pageSize (new query key) -
  // without it the whole list would flash to the loading state on
  // every step instead of extending in place.
  //
  // 2026-07-21. Filtered-count pill mirrors the MAIN LIST query (no
  // `forChipCount: true`) so the number matches the actually-visible
  // list. Before, it inherited the chip-pill contract (which
  // deliberately drops tile filters like `freshToday`, `revertPending`,
  // `bucket`, so each chip pill shows "count if you clicked me
  // instead"). That made the pill overshoot when the operator drilled
  // in from a KPI tile - clicking Today's Fresh Leads (69) then landing
  // on the detail screen showed 1,318 (the date-range scope, not the
  // tile-narrowed set). React-Query still serves this from cache the
  // first time the main list resolves, so no extra round-trip in the
  // common path.
  const totalCountBaseQuery = useMemo<LeadListQuery>(
    () => buildLeadQuery({ statusId, subStatusId }),
    [buildLeadQuery, statusId, subStatusId],
  );
  // Total folds the full no-agent pool in: an assigned-only base + a paired
  // unassigned count that carries the SAME drill-in filters (follow-up dates,
  // revert, large-lead, bucket, status, ...). Only owner-scoped or already-
  // unassigned views skip the fold. The paired query mirrors the whole base
  // query and just swaps the assignment leg, so a filtered drill-in (e.g.
  // "today's follow-up") adds only the no-agent leads that ALSO match that
  // filter - not the entire 326-lead pool, which is what inflated it before.
  // Won/Lost drill-ins stay assigned-only (matching the assigned-only Won/Lost
  // tiles - unassigned closed leads are out of scope), so no fold there.
  const totalFolds =
    totalCountBaseQuery.currentBucket !== 'WON' &&
    totalCountBaseQuery.currentBucket !== 'LOST' &&
    totalCountBaseQuery.assignedAgentIsNull !== true &&
    totalCountBaseQuery.assignedAgentId == null;
  const totalCountQuery = useLeadsList(
    totalFolds
      ? {
          ...totalCountBaseQuery,
          page: 1,
          pageSize: 1,
          includeUnassignedPool: undefined,
          assignedAgentIsNotNull: true,
        }
      : { ...totalCountBaseQuery, page: 1, pageSize: 1 },
    { keepPreviousData: true },
  );
  const totalUnassignedQuery = useLeadsList(
    totalFolds
      ? {
          ...totalCountBaseQuery,
          page: 1,
          pageSize: 1,
          includeUnassignedPool: undefined,
          assignedAgentIsNotNull: undefined,
          assignedAgentIsNull: true,
        }
      : {
          page: 1,
          pageSize: 1,
          sortDir: 'desc',
          currentStatusId: '00000000-0000-0000-0000-000000000000',
        },
    { keepPreviousData: true },
  );
  const totalCount =
    (totalCountQuery.data?.pagination.totalItems ?? 0) +
    (totalFolds ? (totalUnassignedQuery.data?.pagination.totalItems ?? 0) : 0);

  const leadsQuery = useLeadsList(query, { keepPreviousData: true });
  const leads = useMemo(() => {
    const rows = leadsQuery.data?.data ?? [];
    // Adults-only threshold is the source of truth; keep the aside
    // honest even if the API missed `largeLeadQuery` (e.g. stale
    // shared dist) or a denormalized flag drifted.
    if (!largeLeadQuery) return rows;
    return rows.filter((lead) => isLargeLeadQueryByAdults(lead.travellerAdults));
  }, [leadsQuery.data?.data, largeLeadQuery]);

  // Bulk reassign: "Select" next to Total turns the cards into checkboxes and
  // a slim bar at the bottom assigns the ticked ones to one agent. Same roles
  // as the header's owner pencil (LeadOwnerChanger); the server re-checks.
  const canBulkAssign = canBulkAssignLeads(me?.role);
  const [selectMode, setSelectMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkLeads, setBulkLeads] = useState<ReadonlyArray<LeadListItem> | null>(null);
  // Only OPEN leads can change owner; closed cards stay unticked in select mode.
  const assignableLeads = useMemo(() => leads.filter((l) => l.currentBucket === 'OPEN'), [leads]);
  const checkedLeads = useMemo(
    () => leads.filter((l) => checkedIds.has(l.id)),
    [leads, checkedIds],
  );
  const allChecked =
    assignableLeads.length > 0 && assignableLeads.every((l) => checkedIds.has(l.id));
  // A filter change swaps the list: drop ticks on leads no longer shown, so the
  // bar never assigns rows the operator cannot see.
  useEffect(() => {
    setCheckedIds((prev) => {
      const visible = new Set(leads.map((l) => l.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [leads]);
  const toggleChecked = (id: string): void =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const exitSelectMode = (): void => {
    setSelectMode(false);
    setCheckedIds(new Set());
  };
  // 2026-07-21. The `meta.carryoverCount` API hint used to drive the
  // "Carryover (N)" checkbox label in the lens strip - that checkbox
  // was removed per ops, so we no longer read the field here. The
  // server still returns it (backwards compat with other clients) and
  // the per-row "from {month}" ribbon below drives entirely off
  // `stageEnteredAt` vs `monthStartUtcMs`, so nothing needs it here.
  const monthStartUtcMs = useMemo(() => istMonthStartUtcMs(activeMonth), [activeMonth]);
  const previousMonthShortLabel = useMemo(
    () => formatShortMonth(shiftMonth(activeMonth, -1)),
    [activeMonth],
  );

  // Build a live id→status map from the QueryStatus master list. Lead rows
  // look up their dot color through this map so a CMS color edit propagates
  // even before any leads-list cache refresh - the chip and the row read
  // from the same store and can't show different colors for the same stage.
  // See ADR-0032 §18 (also covers the cross-cache invalidation backstop).
  const statusById = useMemo(() => {
    const map = new Map<
      string,
      { color: string | null; bucket: LeadBucket | null; name: string }
    >();
    for (const s of statuses.data) {
      map.set(s.id, {
        color: s.color ?? null,
        bucket: (s.bucket ?? null) as LeadBucket | null,
        name: s.name,
      });
    }
    return map;
  }, [statuses.data]);

  // Banner derives from a status OR a bucket; status wins when both set
  // because it's more specific.
  const activeStatus = statusId ? statuses.data.find((s) => s.id === statusId) : null;
  const activeBucket = bucket;

  // 2026-06-25: Tile dropdown - one-click drill-downs that mirror
  // the Daily Tracker tiles on the pipeline dashboard. Maps to the
  // existing nextFollowUp* / revertPending / requiresFirstContact
  // URL-synced state so deep-links survive.
  // IST calendar date. Must match `LeadsTrackerStrip`'s `todayIso`
  // exactly - the tracker tile drills in with the IST "today" and the
  // aside derives its `currentTile` / dropdown state by comparing
  // against these. Browser-local was previously used; on non-IST
  // browsers it lagged the tile by a day and clicking "Today's Follow
  // Up(s)" showed yesterday's slice.
  const todayIso = useMemo(() => istIsoDate(), []);
  const yesterdayIso = useMemo(() => {
    const now = new Date();
    return istIsoDate(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  }, []);
  // 2026-07-21. Unified filter dropdown. `TileOptionKey` is either a
  // fixed operational tile preset OR a dynamic `stage:<statusId>` /
  // `substage:<subStatusId>` value that mirrors the stage-chip row.
  // This lets the dropdown surface EVERY filter a user can pick from
  // the chips too, so picking a chip syncs the dropdown label and
  // picking a status/sub-status from the dropdown selects the matching
  // chip - a single source of truth.
  //
  // 2026-07-21 (v2). "All leads" removed from the option list to match
  // the inbox side (which has no equivalent "clear" tile - operators
  // clear filters by clicking the Total pill or toggling a chip off).
  // The `'all'` sentinel is still a valid internal state (dropdown
  // shows the "All filters" placeholder when nothing else matches).
  type TileOptionKey =
    | 'all'
    | 'total_tasks'
    | 'today_followups'
    | 'missed_followups'
    | 'fresh_leads'
    | 'revert_pending'
    | 'rejected_approval'
    | 'large_lead_query'
    | 'long_followup'
    | 'unassigned'
    | `stage:${string}`
    | `substage:${string}`;
  // "All leads" reflects the Total Leads KPI click (path='' -> no
  // tile / stage / bucket filter). Ops asked for it to be first-class
  // so the dropdown shows "All leads" selected instead of an empty
  // placeholder when landing from that tile. Everything else operational
  // preset follows, then parent stages, then the Active sub-statuses -
  // same order the chips render below so the dropdown reads
  // left-to-right like the chip rows top-to-bottom.
  const TILE_OPTIONS: ReadonlyArray<{ value: TileOptionKey; label: string }> = useMemo(() => {
    const base: Array<{ value: TileOptionKey; label: string }> = [
      { value: 'all', label: 'All leads' },
      { value: 'total_tasks', label: 'Total tasks (today)' },
      { value: 'today_followups', label: "Today's follow-ups" },
      { value: 'missed_followups', label: 'Missed follow-ups' },
      { value: 'fresh_leads', label: "Today's fresh leads" },
      { value: 'revert_pending', label: 'Revert pending' },
      { value: 'rejected_approval', label: 'Rejected approvals' },
      { value: 'large_lead_query', label: 'Large lead query' },
      { value: 'long_followup', label: 'Long Follow-up' },
      { value: 'unassigned', label: 'Unassigned' },
      // Carry Forward is intentionally NOT a dropdown option. It's a
      // date-window slice of `Active` (createdAt in the CF window),
      // not an orthogonal filter, so adding it to the dropdown made
      // the picker read ambiguously (both "Active" and "Carry forward"
      // could look correct). When landing from the All-leads KPI CF
      // tile the URL carries `?stage=<activeId>&f.carryforward=yes` -
      // the dropdown reflects "Active", and a separate "Carry forward"
      // pill in the active-filter row (below) tells the operator that
      // the createdAt window has been rewritten to the CF slice. See
      // the pill block a few hundred lines down for the render + clear.
    ];
    // Dynamic parent stages (Not Qualified / Active / Won / Lost) so
    // the dropdown offers everything the chip row offers. Long
    // Follow-up is excluded from the chip row so it's excluded here
    // too - it's an auto-promoted parking state, not something ops
    // manually filters to.
    for (const s of parentStatusPills) {
      base.push({ value: `stage:${s.id}` as TileOptionKey, label: s.name });
    }
    // Active sub-statuses (Proposal / Hot / Warm). Only appear when
    // loaded - same async gate as the sub-status chip row below.
    if (proposalSub) {
      base.push({ value: `substage:${proposalSub.id}` as TileOptionKey, label: proposalSub.name });
    }
    if (hotSub) {
      base.push({ value: `substage:${hotSub.id}` as TileOptionKey, label: hotSub.name });
    }
    if (warmSub) {
      base.push({ value: `substage:${warmSub.id}` as TileOptionKey, label: warmSub.name });
    }
    return base;
  }, [parentStatusPills, proposalSub, hotSub, warmSub]);

  // Derive the current dropdown value from the URL-synced state.
  // Priority (matches which state predicate the chips consider "you
  // are here"):
  //   1. sub-status chip selected -> substage:<id>
  //   2. status chip selected -> stage:<id>
  //   3. operational tile filter active
  //   4. fallback -> 'all'
  // 2026-07-21. Chip / dropdown visual state must recognise TWO paths
  // that produce the same logical filter:
  //   1. Aside chip click -> URL `?stage=<wonId>` -> `statusId` set
  //   2. Inbox KPI Won tile click -> URL `?bucket=WON` -> `bucket` set
  //      (no statusId because the KPI URL contract is bucket-based)
  // Without this derivation, path #2 leaves every chip visually
  // unselected and the dropdown reading "All filters" even though
  // `Won ×` shows in the filter-tag row and Total: 5 is correct.
  // Query-building continues to consume `statusId` and `bucket`
  // directly - this fallback is display-only.
  const bucketDerivedStatusId = useMemo<string | null>(() => {
    if (bucket !== 'WON' && bucket !== 'LOST') return null;
    return parentStatusPills.find((p) => p.bucket === bucket)?.id ?? null;
  }, [bucket, parentStatusPills]);
  const effectiveStatusId = statusId ?? bucketDerivedStatusId;

  const currentTile: TileOptionKey = useMemo(() => {
    // URL-flag lenses win - they clear stage/sub-stage selection when picked.
    if (unassignedOnly) return 'unassigned';
    if (longFollowupOnly) return 'long_followup';
    if (dailyTaskPool) return 'total_tasks';
    if (subStatusId) return `substage:${subStatusId}` as TileOptionKey;
    // Carry Forward is NOT its own dropdown option (see TILE_OPTIONS).
    // A CF-active URL (`?stage=<activeId>&f.carryforward=yes`) still
    // reads as `stage:<activeId>` here - the dropdown shows "Active"
    // and a separate "Carry forward" pill in the active-filter row
    // conveys the CF window narrowing without inventing a bespoke
    // dropdown value that would ambiguously overlap with Active.
    if (effectiveStatusId && !revertPending && !rejectedApproval) {
      return `stage:${effectiveStatusId}` as TileOptionKey;
    }
    if (revertPending) return 'revert_pending';
    if (rejectedApproval) return 'rejected_approval';
    if (largeLeadQuery) return 'large_lead_query';
    // `freshToday` (tracker "Today's Fresh Leads" tile) and the
    // legacy `requiresFirstContact` both land under this preset.
    if (requiresFirstContact || freshToday) return 'fresh_leads';
    if (nextFollowUpFrom === todayIso && nextFollowUpTo === todayIso) return 'today_followups';
    if (nextFollowUpTo === yesterdayIso && !nextFollowUpFrom) return 'missed_followups';
    return 'all';
  }, [
    carryforwardOnly,
    effectiveStatusId,
    subStatusId,
    revertPending,
    rejectedApproval,
    largeLeadQuery,
    requiresFirstContact,
    freshToday,
    dailyTaskPool,
    nextFollowUpFrom,
    nextFollowUpTo,
    todayIso,
    yesterdayIso,
    unassignedOnly,
    longFollowupOnly,
  ]);
  // Clear operational tile flags when the operator picks a stage chip so
  // the list isn't silently narrowed by a leftover tracker drill-in.
  //
  // 2026-07-21. Also drops the `f.carryforward` URL flag - chip clicks
  // and operational-tile picks all mean the operator has moved away
  // from the CF slice. Without this, picking (say) the Not Qualified
  // chip from CF state left the flag in the URL so the query stayed
  // "NQ in CF window", which is not what the click implied.
  /**
   * Drops the carry-forward flag from the URL.
   *
   * Must read the LIVE URL: `f.carryforward` is a GRID key, written by nuqs
   * straight to History, so React Router's params snapshot usually doesn't
   * have it - a guard reading that snapshot would decide there was nothing to
   * clear and leave the flag in place.
   */
  const clearCarryforward = (): void => {
    writeParams((p) => {
      p.delete('f.carryforward');
      p.delete('carryforward');
    });
  };

  const clearTileFilters = (): void => {
    setRevertPending(false);
    setRejectedApproval(false);
    setLargeLeadQuery(false);
    setRequiresFirstContact(false);
    setFreshToday(false);
    setDailyTaskPool(false);
    setNextFollowUpFrom(null);
    setNextFollowUpTo(null);
    setParams(
      (prev) => {
        if (
          !prev.has('f.carryforward') &&
          !prev.has('carryforward') &&
          !prev.has('f.unassigned') &&
          !prev.has('f.longFollowup')
        )
          return prev;
        const p = new URLSearchParams(prev);
        p.delete('f.carryforward');
        p.delete('carryforward');
        p.delete('f.unassigned');
        p.delete('f.longFollowup');
        return p;
      },
      { replace: true },
    );
  };

  // Exactly one chip selected at a time among:
  //   parent status · Revert Pending · sub-status (Proposal/Hot/Warm)
  // Picking one clears the others so Revert Pending + Proposal cannot
  // both appear selected.
  const selectParentStatus = (id: string): void => {
    clearTileFilters();
    setSubStatusId(null);
    // Toggle-off must work in a single click whether the chip's active
    // state came from `statusId` (aside chip earlier) or `bucket` (KPI
    // tile URL). Reading `effectiveStatusId` covers both; without this,
    // a URL-in bucket=WON would need TWO clicks to clear because the
    // first click sets statusId=wonId (matching the "not currently
    // selected" branch below).
    if (effectiveStatusId === id) {
      setStatusId(null);
      setBucket(null);
      return;
    }
    const pill = parentStatusPills.find((s) => s.id === id);
    // Won / Lost sit outside the OPEN pipeline - drop Active pipeline
    // (bucket=OPEN) so the list isn't empty (Won ∩ OPEN = 0).
    if (pill?.bucket === 'WON' || pill?.bucket === 'LOST') {
      setBucket(null);
    }
    setStatusId(id);
  };

  // Deep-links / leftover URL can still carry bucket=OPEN with a Won/Lost
  // stage chip - scrub Active pipeline whenever that combo is active.
  useEffect(() => {
    if (!statusId || bucket !== 'OPEN') return;
    const pill = parentStatusPills.find((s) => s.id === statusId);
    if (pill?.bucket === 'WON' || pill?.bucket === 'LOST') {
      setBucket(null);
    }
  }, [statusId, bucket, parentStatusPills]);
  const selectSubStatus = (id: string): void => {
    clearTileFilters();
    setStatusId(null);
    setSubStatusId((prev) => (prev === id ? null : id));
  };
  const selectRevertPending = (): void => {
    const wasSelected = revertPending && !statusId && !subStatusId;
    clearTileFilters();
    setStatusId(null);
    setSubStatusId(null);
    if (!wasSelected) {
      setRevertPending(true);
      setBucket('OPEN');
    }
  };
  // Rejected Approvals is not OPEN-only (a Won/Lost lead can still carry a
  // rejected latest status-change request), so unlike Revert Pending this
  // does not force `bucket=OPEN` - the server-side `rejectedApproval` filter
  // is self-contained.
  const selectRejectedApproval = (): void => {
    const wasSelected = rejectedApproval && !statusId && !subStatusId;
    clearTileFilters();
    setStatusId(null);
    setSubStatusId(null);
    if (!wasSelected) {
      setRejectedApproval(true);
    }
  };

  // 2026-07-21. Unified filter dropdown handler. Declared AFTER
  // `selectParentStatus` / `selectSubStatus` so the stage/substage
  // branches route to the same setters the chip clicks use - one code
  // path, no drift.
  const onTileChange = (next: TileOptionKey): void => {
    // Every dropdown pick moves the operator away from the CF slice
    // (CF isn't a dropdown option). Clear the URL flag up-front so
    // state-driven branches below (chip clicks, operational presets)
    // don't need to know about it - one line, one place.
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('f.carryforward');
        p.delete('carryforward');
        // Unassigned / Long Follow-up are URL-flag lenses: set the picked one,
        // clear both for any other pick (stage, sub-stage, or preset).
        if (next === 'unassigned') p.set('f.unassigned', 'yes');
        else p.delete('f.unassigned');
        if (next === 'long_followup') p.set('f.longFollowup', 'yes');
        else p.delete('f.longFollowup');
        return p;
      },
      { replace: true },
    );

    if (next.startsWith('substage:')) {
      selectSubStatus(next.slice('substage:'.length));
      return;
    }
    if (next.startsWith('stage:')) {
      selectParentStatus(next.slice('stage:'.length));
      return;
    }
    // Operational preset - clear any stage-chip selection so exactly
    // one filter story is active.
    setStatusId(null);
    setSubStatusId(null);
    setRevertPending(next === 'revert_pending');
    setRejectedApproval(next === 'rejected_approval');
    setLargeLeadQuery(next === 'large_lead_query');
    // 2026-07-21. "Today's fresh leads" pick MUST match the Daily
    // Tracker KPI tile of the same name: leads CREATED today (small
    // per-day count). Previously the dropdown set
    // `requiresFirstContact=true` instead, which asks a totally
    // different question - "never contacted, any time in the current
    // date range" - and returned thousands of rows against the KPI's
    // handful. Same label, two semantics; landing from the KPI vs
    // picking from the dropdown produced wildly different totals.
    // `freshToday=true` mirrors what the KPI tile URL sends, so both
    // paths land on the same result set. `requiresFirstContact` state
    // is still parsed from URL for legacy deep-links, and the currentTile
    // derivation below still recognises it as the same 'fresh_leads'
    // dropdown value.
    setRequiresFirstContact(false);
    setFreshToday(next === 'fresh_leads');
    setDailyTaskPool(next === 'total_tasks');
    setNextFollowUpFrom(next === 'today_followups' ? todayIso : null);
    setNextFollowUpTo(
      next === 'today_followups' ? todayIso : next === 'missed_followups' ? yesterdayIso : null,
    );
    if (next === 'revert_pending') setBucket('OPEN');
    else if (
      next === 'all' ||
      next === 'unassigned' ||
      next === 'long_followup' ||
      next === 'total_tasks'
    )
      setBucket(null);
  };

  // Stage chips are laid out in three fixed rows (ops layout). Pick the
  // specific parent statuses out of the list so each lands in its row.
  const notQualifiedPill = parentStatusPills.find((s) => s.code === 'not_qualified');
  const activePill = parentStatusPills.find((s) => s.code === 'active');
  const wonPill = parentStatusPills.find((s) => (s.bucket ?? null) === 'WON');
  const lostPill = parentStatusPills.find((s) => (s.bucket ?? null) === 'LOST');
  const renderParentChip = (s: (typeof parentStatusPills)[number]) => (
    <StageChip
      key={s.id}
      // In CF state (`?stage=<activeId>&f.carryforward=yes`) the Active
      // chip stays highlighted because CF is a date-slice of Active,
      // not a competing parent stage - the dropdown reads "Active" too.
      // The separate "Carry forward" pill in the active-filter row is
      // what tells the operator the CF window is on top.
      selected={effectiveStatusId === s.id && !subStatusId && !revertPending && !rejectedApproval}
      onClick={() => selectParentStatus(s.id)}
      bucket={(s.bucket ?? null) as LeadBucket | null}
      statusName={s.name}
      color={s.color ?? null}
    >
      {shortStageName(s.name)}{' '}
      <StatusCount
        rptExplicit={rptExplicit}
        query={buildLeadQuery({ statusId: s.id, subStatusId: null }, { forChipCount: true })}
      />
    </StageChip>
  );

  return (
    <aside className="flex h-full min-h-0 flex-col border-r border-zinc-200 bg-white">
      <div className="space-y-1.5 border-b border-zinc-200 p-2.5">
        {/* Date-range picker removed 2026-08-01 (fixed this-month + carry-forward
            snapshot; historical filtering lives in Reports). The header row now
            holds the owner ("All owners") scope + the lens dropdown side by side,
            each taking half the width. */}
        <div className="flex items-start gap-1.5">
          {/* Owner (assigned agent) scope - mirrors the Lead Inbox "All owners"
              picker. Hidden for SALES_AGENT (they only ever see their own leads). */}
          <OwnerFilter
            value={ownerId}
            onValueChange={setOwnerId}
            className="min-w-0 flex-1"
            triggerClassName="h-8 text-xs"
          />
          {/* 2026-06-25: Tile/lens dropdown - quick drill-down across the same
              lenses as the Daily Tracker tiles. `min-w-0` lets long labels
              ellipsize inside the h-8 pill instead of wrapping. */}
          <UzSelect<TileOptionKey>
            name="aside-tile"
            triggerClassName="h-8 truncate text-xs"
            options={TILE_OPTIONS}
            value={currentTile}
            onValueChange={(v) => onTileChange(v ?? 'all')}
            containerClassName="min-w-0 flex-1"
          />
        </div>
        <UzInput
          name="aside-search"
          className="h-8 text-xs"
          placeholder="Search name, ID, phone, email…"
          leftAdornment={<Search className="h-4 w-4" />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {/* Destination scope. Its own full-width row under search (the owner /
            lens row and the intent / Total row are already full); searchable
            because the master runs to 200+ names. Clear (X) = all destinations. */}
        <UzCombobox
          name="aside-destination"
          placeholder="All destinations"
          searchPlaceholder="Search destination…"
          emptyText="No destination matches"
          triggerClassName="h-8 text-xs"
          options={destinations.options}
          loading={destinations.isLoading}
          value={destinationFilter}
          onValueChange={setDestinationFilter}
          clearable
          onClear={() => setDestinationFilter('')}
        />
        {/* Stage chips - one selected at a time, flowing one after another
            (no fixed rows) so they pack into as few lines as fit:
              Not Qualified · Won · Lost · Active · Revert Pending ·
              Rejected · Proposal · Hot · Warm. The groups below use
              `contents` so they keep their order without forcing a break. */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {/* Not Qualified · Won · Lost */}
          <div className="contents">
            {notQualifiedPill && renderParentChip(notQualifiedPill)}
            {wonPill && renderParentChip(wonPill)}
            {lostPill && renderParentChip(lostPill)}
          </div>
          {/* Active + Revert Pending. Carry Forward is
              intentionally NOT a chip - it's a date-window narrowing
              of Active (see the "Carry forward" pill in the
              active-filter row below), not a peer stage. Adding it
              here made the chip row read as if CF were an alternative
              to Active when it's really a slice of it. */}
          <div className="contents">
            {activePill && renderParentChip(activePill)}
            <StageChip
              selected={revertPending && !statusId && !subStatusId}
              onClick={selectRevertPending}
              bucket={'OPEN'}
              statusName="Revert Pending"
              color={null}
            >
              Revert Pending{' '}
              <StatusCount
                rptExplicit={rptExplicit}
                query={{
                  ...buildLeadQuery({ statusId: null, subStatusId: null }, { forChipCount: true }),
                  revertPending: true,
                  currentBucket: 'OPEN',
                  ...(notQualifiedStatusId ? { excludeStatusId: notQualifiedStatusId } : {}),
                }}
              />
            </StageChip>
            {/* Rejected Approvals - mirrors Revert Pending's wiring, but is
                NOT OPEN-only (a Won/Lost lead can still carry a rejected
                latest status-change request) and, unlike Revert Pending,
                does NOT carve out Not Qualified - a rejected-approval lead
                sitting in Not Qualified must still count. Dot forced to the
                same red used for the Lost chip so it reads as "rejected". */}
            <StageChip
              selected={rejectedApproval && !statusId && !subStatusId}
              onClick={selectRejectedApproval}
              bucket={null}
              statusName="Rejected Approvals"
              color="#ef4444"
            >
              Rejected{' '}
              <StatusCount
                rptExplicit={rptExplicit}
                query={{
                  ...buildLeadQuery({ statusId: null, subStatusId: null }, { forChipCount: true }),
                  rejectedApproval: true,
                }}
              />
            </StageChip>
          </div>
          {/* Active sub-statuses (Proposal / Hot / Warm). */}
          <div className="contents">
            {proposalSub && (
              <StageChip
                selected={subStatusId === proposalSub.id && !revertPending && !rejectedApproval}
                onClick={() => selectSubStatus(proposalSub.id)}
                bucket={'OPEN'}
                statusName={proposalSub.name}
                color={proposalSub.color ?? null}
              >
                Proposal{' '}
                <StatusCount
                  rptExplicit={rptExplicit}
                  query={buildLeadQuery(
                    { statusId: null, subStatusId: proposalSub.id },
                    { forChipCount: true },
                  )}
                />
              </StageChip>
            )}
            {hotSub && (
              <StageChip
                selected={subStatusId === hotSub.id && !revertPending && !rejectedApproval}
                onClick={() => selectSubStatus(hotSub.id)}
                bucket={'OPEN'}
                statusName={hotSub.name}
                color={hotSub.color ?? null}
              >
                Hot{' '}
                <StatusCount
                  rptExplicit={rptExplicit}
                  query={buildLeadQuery(
                    { statusId: null, subStatusId: hotSub.id },
                    { forChipCount: true },
                  )}
                />
              </StageChip>
            )}
            {warmSub && (
              <StageChip
                selected={subStatusId === warmSub.id && !revertPending && !rejectedApproval}
                onClick={() => selectSubStatus(warmSub.id)}
                bucket={'OPEN'}
                statusName={warmSub.name}
                color={warmSub.color ?? null}
              >
                Warm{' '}
                <StatusCount
                  rptExplicit={rptExplicit}
                  query={buildLeadQuery(
                    { statusId: null, subStatusId: warmSub.id },
                    { forChipCount: true },
                  )}
                />
              </StageChip>
            )}
          </div>
          {/* Filtered count - right-aligned on its own row below the
              chips. Reflects every active filter above (date range,
              owner, tile / stage / sub-status).
              2026-07-21 history:
                - v1: renamed "Total" -> "Filtered leads" for clarity.
                - v2: swapped `<button>` for `<div>` because operators
                      misread the label as an interactive control.
                - v3: dropped the `title` tooltip + tightened the wrapper
                      (no gap-0.5 column, no pt-2 pb-1 padding) so the
                      pill reads as a compact static label - no hover
                      affordance, no extra vertical space between the
                      chip row and this line, and no visible gap below
                      before the lens strip. Clearing filters happens
                      through the dropdown "All leads" pick or an
                      individual pill's X; this element is display
                      only. */}
          {/* Lead Intent filter (left) + filtered Total (right), spaced apart. */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <UzSelect<string>
              name="aside-intent"
              triggerClassName="h-8 truncate text-xs"
              options={INTENT_FILTER_OPTIONS}
              value={intentFilter}
              onValueChange={(v) => setIntentFilter(v ?? 'ANY')}
              containerClassName="w-40"
            />
            <span className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-zinc-700">
              Total:
              <span className="font-bold text-zinc-900">
                {totalCountQuery.isLoading ? '…' : totalCount.toLocaleString('en-IN')}
              </span>
            </span>
            {/* Icon-only so the row keeps its two-item look; the bar it opens
                at the bottom carries the words. */}
            {canBulkAssign && (
              <UzTooltip
                side="bottom"
                align="end"
                label={
                  selectMode
                    ? 'Stop selecting'
                    : 'Bulk assign - select leads and assign them to an agent'
                }
              >
                <button
                  type="button"
                  onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                  aria-pressed={selectMode}
                  aria-label="Select leads to assign"
                  className={
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition-colors ' +
                    (selectMode
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900')
                  }
                >
                  <ListChecks className="h-4 w-4" />
                </button>
              </UzTooltip>
            )}
          </div>
        </div>
      </div>

      {/* Active-this-month lens strip. The default view: today is in
          June, so the operator sees only OPEN+active leads whose stage
          was entered this month (with last-month leftovers ribboned
          and counted). Prev/next chevrons navigate months without
          touching any other filter; the carryover toggle and the
          dismiss-X let the operator drop into a non-lens view if they
          want the pre-existing range-based behaviour. Suppressed
          automatically when another filter (bucket=WON, a status
          chip, an explicit date range, etc.) would conflict with
          what the lens is showing. */}
      {lensActive && (
        // 2026-07-21. Prev/next month chevrons and Carryover checkbox
        // removed per ops - both were interactive surfaces in the aside
        // that produced counts diverging from the KPI tiles (e.g.
        // toggling Carryover here surfaced 5k+ pre-cutover backlog rows
        // while the corresponding KPI showed a much smaller number).
        // The lens is now DISPLAY-ONLY in the aside: the month label
        // reads as a passive indicator of what the current view is
        // scoped to; the dismiss X still exits the lens if the operator
        // wants the plain range view instead. `includeCarryover` state
        // + URL param (`?carry=1`) are kept for backwards compat with
        // any deep-link that already carries them, but the aside no
        // longer offers a control to flip them on.
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 bg-blue-50/50 px-3 py-2 text-[11.5px]">
          <span className="font-medium text-zinc-900">{formatMonth(activeMonth)}</span>
          <span className="text-zinc-500">- active leads</span>
          <button
            type="button"
            onClick={() => setLensEnabled(false)}
            className="ml-auto rounded p-0.5 text-zinc-500 hover:bg-white hover:text-zinc-900"
            aria-label="Hide active-this-month lens"
            title="Switch back to the date-range view"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {!lensEnabled && (
        <div className="border-b border-zinc-200 bg-zinc-50/80 px-3 py-1.5 text-[11px] text-zinc-500">
          <button
            type="button"
            onClick={() => setLensEnabled(true)}
            className="font-medium text-blue-700 hover:underline"
          >
            Switch back to active-this-month view
          </button>
        </div>
      )}

      {/* Active-filter pills. Each pill represents one tile-driven
          (or chip-driven) narrowing; clicking the X clears just
          that one. Multiple can stack at once - e.g. a Today's
          Follow-Up tile click lands here with bucket=OPEN AND
          nextFollowUpFrom/To set, both shown as separate pills. */}
      {(() => {
        type Pill = { label: string; tint: string; onClear: () => void };
        const pills: Pill[] = [];
        if (activeStatus) {
          pills.push({
            label: activeStatus.name,
            tint: 'border-zinc-200 bg-zinc-50 text-zinc-700',
            onClear: () => setStatusId(null),
          });
        }
        // 2026-07-21. Only surface bucket pills for the terminal buckets
        // (WON / LOST) - those are semantic filters the operator picks.
        // The `OPEN` bucket is implicit context for every operational
        // tile (Revert Pending, Follow-ups, Fresh Today, Carry Forward,
        // Large Lead Query) - showing a separate "Active pipeline" pill
        // alongside e.g. "Revert pending" duplicates the same idea and
        // makes the filter bar read as two independent filters when it
        // isn't. Terminal buckets stay pilled because they're the only
        // way to represent "Won" / "Lost" without a full stage chip.
        if (activeBucket && activeBucket !== 'OPEN') {
          const bucketBanner = BUCKET_BANNER[activeBucket];
          if (bucketBanner) {
            pills.push({
              label: bucketBanner.label,
              tint: bucketBanner.tint,
              onClear: () => setBucket(null),
            });
          }
        }
        if (revertPending) {
          pills.push({
            label: 'Revert pending',
            tint: 'border-zinc-200 bg-zinc-50 text-zinc-700',
            onClear: () => setRevertPending(false),
          });
        }
        if (rejectedApproval) {
          pills.push({
            label: 'Rejected approvals',
            tint: 'border-red-200 bg-red-50 text-red-800',
            onClear: () => setRejectedApproval(false),
          });
        }
        if (largeLeadQuery) {
          pills.push({
            label: 'Large lead query',
            tint: 'border-violet-200 bg-violet-50 text-violet-800',
            onClear: () => setLargeLeadQuery(false),
          });
        }
        if (requiresFirstContact) {
          pills.push({
            label: 'First contact pending',
            tint: 'border-zinc-200 bg-zinc-50 text-zinc-700',
            onClear: () => setRequiresFirstContact(false),
          });
        }
        if (freshToday) {
          pills.push({
            label: "Today's fresh leads",
            tint: 'border-blue-200 bg-blue-50 text-blue-800',
            onClear: () => setFreshToday(false),
          });
        }
        if (dailyTaskPool) {
          pills.push({
            label: 'Total tasks (today)',
            tint: 'border-zinc-200 bg-zinc-50 text-zinc-700',
            onClear: () => setDailyTaskPool(false),
          });
        }
        if (carryforwardOnly) {
          // 2026-07-21. Carry Forward pill. Landing here from the
          // All-leads KPI CF tile (URL: `?stage=<activeId>&f.carryforward=yes`)
          // sets the dropdown to "Active" and highlights the Active
          // chip - this pill is the visible signal that the createdAt
          // window has been narrowed to the CF slice. Click X to drop
          // just the CF flag (keeps the Active stage selection so the
          // operator lands in plain Active-this-month instead of
          // "no filter").
          pills.push({
            label: 'Carry forward',
            tint: 'border-indigo-200 bg-indigo-50 text-indigo-800',
            onClear: clearCarryforward,
          });
        }
        if (nextFollowUpFrom || nextFollowUpTo) {
          // Single pill covers either side of the range - clearing
          // wipes both because that's the granularity the tiles
          // think in (Today's Follow-Up sets both = today; Missed
          // sets only `To`).
          const label = (() => {
            if (nextFollowUpFrom && nextFollowUpTo && nextFollowUpFrom === nextFollowUpTo) {
              return `Follow-up on ${nextFollowUpFrom}`;
            }
            if (nextFollowUpFrom && nextFollowUpTo) {
              return `Follow-up ${nextFollowUpFrom} → ${nextFollowUpTo}`;
            }
            if (nextFollowUpTo) return `Follow-up overdue ≤ ${nextFollowUpTo}`;
            return `Follow-up from ${nextFollowUpFrom}`;
          })();
          pills.push({
            label,
            tint: 'border-zinc-200 bg-zinc-50 text-zinc-700',
            onClear: () => {
              setNextFollowUpFrom(null);
              setNextFollowUpTo(null);
            },
          });
        }
        if (pills.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1.5 border-b border-zinc-200 px-3 py-2">
            {pills.map((p) => (
              <div
                key={p.label}
                className={
                  'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-medium ' +
                  p.tint
                }
              >
                <span className="truncate">{p.label}</span>
                <button
                  type="button"
                  className="shrink-0 rounded p-0.5 hover:bg-white/50"
                  onClick={p.onClear}
                  aria-label={`Clear ${p.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        );
      })()}

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {leadsQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-zinc-500">
            <UzSpinner size="sm" /> Loading leads…
          </div>
        ) : leads.length === 0 ? (
          <EmptyState
            lensActive={lensActive}
            lensEnabled={lensEnabled}
            includeCarryover={includeCarryover}
            activeMonthLabel={formatMonth(activeMonth)}
            onIncludeCarryover={() => setIncludeCarryover(true)}
            onResetLens={() => {
              setLensEnabled(true);
              setActiveMonth(currentIstMonthKey());
              setIncludeCarryover(true);
            }}
          />
        ) : (
          <>
            <ul className="flex flex-col gap-1">
              {leads.map((lead, idx) => {
                const liveStatus = statusById.get(lead.currentStatus.id);
                // Carryover ribbon: show only when the lens is active AND
                // the row's stage was entered before the chosen month.
                // Without the lens, "from May" wouldn't make sense - we
                // only ribbon rows we know are out-of-current-month.
                const isCarryover =
                  lensActive &&
                  includeCarryover &&
                  new Date(lead.stageEnteredAt).getTime() < monthStartUtcMs;
                return (
                  <li key={lead.id}>
                    <LeadRow
                      lead={lead}
                      ordinal={idx + 1}
                      selected={lead.id === selectedLeadId}
                      onClick={() => (selectMode ? toggleChecked(lead.id) : onSelect(lead))}
                      check={
                        selectMode
                          ? {
                              checked: checkedIds.has(lead.id),
                              disabled: lead.currentBucket !== 'OPEN',
                            }
                          : null
                      }
                      statusOverride={liveStatus ?? null}
                      carryoverFromLabel={isCarryover ? previousMonthShortLabel : null}
                    />
                  </li>
                );
              })}
            </ul>
            {/* Load more - server-side: grows the query's pageSize by a
                step (see LIST_PAGE_STEP). Shown while the server holds
                more rows than the current slice, up to the API cap. */}
            {(() => {
              const fetched = leadsQuery.data?.data.length ?? 0;
              const total = leadsQuery.data?.pagination.totalItems ?? 0;
              const atCap = listPageSize >= PAGINATION.MAX_PAGE_SIZE;
              if (total <= fetched) return null;
              return (
                <div className="px-1 pt-2 pb-1">
                  {atCap ? (
                    <p className="text-center text-[11px] text-zinc-400">
                      Showing the first {fetched} of {total} - narrow the filters to see the rest.
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="w-full rounded-md border border-zinc-200 bg-white py-1.5 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60"
                      disabled={leadsQuery.isFetching}
                      onClick={() =>
                        setListPageSize((s) =>
                          Math.min(s + LIST_PAGE_STEP, PAGINATION.MAX_PAGE_SIZE),
                        )
                      }
                    >
                      {leadsQuery.isFetching ? (
                        <span className="inline-flex items-center gap-1.5">
                          <UzSpinner size="sm" /> Loading…
                        </span>
                      ) : (
                        'Load more'
                      )}
                    </button>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Selection bar - only in select mode, pinned under the list so the
          filters above stay exactly as they were. */}
      {selectMode && (
        <div className="flex items-center gap-2 border-t border-zinc-200 bg-white px-3 py-2">
          <button
            type="button"
            role="checkbox"
            aria-checked={allChecked ? true : checkedLeads.length > 0 ? 'mixed' : false}
            disabled={assignableLeads.length === 0}
            onClick={() =>
              setCheckedIds(allChecked ? new Set() : new Set(assignableLeads.map((l) => l.id)))
            }
            title="Select every open lead loaded in the list"
            className="flex items-center gap-1.5 rounded px-1 py-0.5 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          >
            <CheckBox checked={allChecked} />
            All
          </button>
          <span className="truncate text-xs text-zinc-600 tabular-nums">
            {checkedLeads.length > 0 ? `${checkedLeads.length} selected` : 'Tap leads to select'}
          </span>
          <UzButton
            size="sm"
            className="ml-auto"
            disabled={checkedLeads.length === 0}
            onClick={() => setBulkLeads(checkedLeads)}
          >
            Assign
          </UzButton>
          <button
            type="button"
            onClick={exitSelectMode}
            aria-label="Stop selecting"
            className="rounded p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <LeadBulkReassignDialog
        leads={bulkLeads}
        onClose={() => setBulkLeads(null)}
        onDone={() => {
          setBulkLeads(null);
          exitSelectMode();
        }}
      />
    </aside>
  );
};

/** Visual-only tick box: the row / bar button around it is the control. */
const CheckBox = ({
  checked,
  disabled,
}: {
  checked: boolean;
  disabled?: boolean;
}): React.JSX.Element => (
  <span
    aria-hidden
    className={
      'flex h-4 w-4 shrink-0 items-center justify-center rounded border ' +
      (checked
        ? 'border-blue-600 bg-blue-600 text-white'
        : disabled
          ? 'border-zinc-200 bg-zinc-100'
          : 'border-zinc-300 bg-white')
    }
  >
    {checked && <Check className="h-3 w-3" strokeWidth={3} />}
  </span>
);

// ---------- Empty state ----------

interface EmptyStateProps {
  lensActive: boolean;
  lensEnabled: boolean;
  includeCarryover: boolean;
  activeMonthLabel: string;
  onIncludeCarryover: () => void;
  onResetLens: () => void;
}

/**
 * Empty state for the inbox list. When the lens is active it tailors
 * the copy + the suggested next action to the current state - so an
 * agent whose plate is empty for the chosen month gets a single-click
 * shortcut to pull in last month's leftovers, instead of a dead-end
 * "no leads" message. Falls back to the plain neutral string when the
 * lens is off (the existing date-range view didn't have a special
 * empty state and we don't want to regress that path).
 */
const EmptyState = ({
  lensActive,
  lensEnabled,
  includeCarryover,
  activeMonthLabel,
  onIncludeCarryover,
  onResetLens,
}: EmptyStateProps): React.JSX.Element => {
  if (!lensActive) {
    return (
      <div className="px-4 py-8 text-center text-sm text-zinc-500">
        No leads match these filters.
      </div>
    );
  }
  return (
    <div className="space-y-2 px-4 py-8 text-center text-sm text-zinc-500">
      <p>No active leads in {activeMonthLabel} for your visibility.</p>
      {!includeCarryover ? (
        <button
          type="button"
          onClick={onIncludeCarryover}
          className="text-[12px] font-medium text-blue-700 hover:underline"
        >
          Include carryover from previous months
        </button>
      ) : (
        <button
          type="button"
          onClick={onResetLens}
          className="text-[12px] font-medium text-blue-700 hover:underline"
        >
          Reset to {lensEnabled ? 'this month' : 'active-this-month'}
        </button>
      )}
    </div>
  );
};

// ---------- Chip ----------

interface StageChipProps {
  children: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  bucket?: LeadBucket | null;
  /** Status name used to infer a tone when `bucket` and `color` are NULL. */
  statusName?: string;
  /** Editable hex color from the QueryStatus master (ADR-0032 §18). */
  color?: string | null;
}

/**
 * 2026-06-25: small live-count badge rendered inside each StageChip's
 * children. Fires a cheap `pageSize: 1` lead-list query that scopes
 * to the chip's status/sub-status under the SAME filter context as
 * the main list (date range, search, bucket, tile drill-down). The
 * server returns `pagination.totalItems` which gives an exact count
 * without paying for the row payload.
 *
 * Multiple instances share the React Query cache via the matching
 * key, so a chip that's also the currently-active filter doesn't
 * fire a second request - it reads the existing main-list query
 * result by the same shape.
 */
const StatusCount = ({
  query,
  rptExplicit = false,
}: {
  query: LeadListQuery;
  /** Report-tile drill: the chip must reflect the drilled WINDOW, so skip the
   *  global (unwindowed) unassigned fold and use the single windowed query
   *  (which already folds unassigned via `includeUnassignedPool`). */
  rptExplicit?: boolean;
}): React.JSX.Element => {
  // `query` is already built for this chip's selection (via buildLeadQuery), so
  // it carries the correct lens / date-range / status context. We only override
  // the page size to 1 - `pagination.totalItems` gives the exact count without
  // paying for the row payload.
  //
  // OPEN status / sub-status chips (NQ, Active, Proposal, Hot, Warm) fold the
  // FULL no-agent pool of that status in - assigned-only base + a paired
  // unassigned count - so the brackets match the All-leads KPI cards + Daily
  // Tracker (which include never-assigned leads, not just the exit-released
  // subset). Terminal chips (Won / Lost), the Unassigned chip itself, and the
  // plain All view are left as-is (their pools are already right).
  const NONE_ID = '00000000-0000-0000-0000-000000000000';
  const isOpenStatusChip =
    !rptExplicit &&
    (query.currentSubStatusId != null || query.currentStatusId != null) &&
    query.currentBucket !== 'WON' &&
    query.currentBucket !== 'LOST' &&
    query.assignedAgentIsNull !== true &&
    // When a specific owner is picked the count is owner-scoped, so the global
    // no-agent pool must NOT be folded in (it belongs to no owner).
    query.assignedAgentId == null;
  const q = useLeadsList(
    isOpenStatusChip
      ? {
          ...query,
          page: 1,
          pageSize: 1,
          includeUnassignedPool: undefined,
          assignedAgentIsNotNull: true,
        }
      : { ...query, page: 1, pageSize: 1 },
  );
  // Paired full-unassigned count for this status/sub-status (0 for non-folding
  // chips - it queries the sentinel id and returns nothing).
  const uq = useLeadsList(
    isOpenStatusChip
      ? {
          page: 1,
          pageSize: 1,
          sortDir: 'desc',
          excludeWebsite: true,
          assignedAgentIsNull: true,
          ...(query.currentSubStatusId
            ? { currentSubStatusId: query.currentSubStatusId }
            : { currentStatusId: query.currentStatusId }),
        }
      : { page: 1, pageSize: 1, sortDir: 'desc', currentStatusId: NONE_ID },
  );
  const count =
    (q.data?.pagination.totalItems ?? 0) +
    (isOpenStatusChip ? (uq.data?.pagination.totalItems ?? 0) : 0);
  const loading = q.isLoading || (isOpenStatusChip && uq.isLoading);
  // Inherit chip text color so the count stays white on the selected
  // (dark) chip - do not use a fixed muted color that overrides `text-white`.
  return <span className="ml-1 text-[11px] text-current">({loading ? '…' : count})</span>;
};

const StageChip = ({
  children,
  selected,
  onClick,
  bucket,
  statusName,
  color,
}: StageChipProps): React.JSX.Element => {
  // The "All" chip has neither statusName nor color and intentionally
  // renders no dot - it's the cleared/global filter.
  const showDot = Boolean(statusName);
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        // 2px tighter padding than default (px 10→8, py 4→2); text stays 11px
        'inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[11px] font-medium transition-colors ' +
        (selected
          ? 'border-zinc-900 bg-zinc-900 text-white'
          : 'border-zinc-200 bg-zinc-50 text-zinc-600 hover:border-zinc-300 hover:text-zinc-900')
      }
    >
      {showDot && (
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={stageDotStyle(color ?? null, bucket ?? null, statusName ?? '')}
          aria-hidden
        />
      )}
      {children}
    </button>
  );
};

// ---------- Row ----------

interface LeadRowProps {
  lead: LeadListItem;
  /**
   * 1-based position in the visible list. Rendered as a small number
   * on the row so operators can reference leads by position ("check
   * number 7") and track where they are as "Load more" extends the
   * list.
   */
  ordinal: number;
  selected: boolean;
  onClick: () => void;
  /**
   * Live status master snapshot (id-matched against the QueryStatus options
   * cache). Used to compute the dot color so a CMS color edit propagates
   * without waiting on a leads-list refresh. Falls back to the embedded
   * `lead.currentStatus` when the live row isn't available (e.g. a status
   * deleted out from under the lead).
   */
  statusOverride?: { color: string | null; bucket: LeadBucket | null; name: string } | null;
  /**
   * Short-month label (e.g. "May 2026") when this row is a carryover
   * from a previous month under the active-this-month lens. NULL when
   * the row is in-month, or when the lens isn't active. Renders as a
   * tiny zinc chip in the row's secondary line so the operator can
   * spot prior-month work at a glance.
   */
  carryoverFromLabel?: string | null;
  /**
   * Select mode (bulk assign): the ordinal becomes a tick box and the row
   * toggles instead of opening the lead. `disabled` = closed lead, which cannot
   * change owner. Null outside select mode.
   */
  check?: { checked: boolean; disabled: boolean } | null;
}

const LeadRow = ({
  lead,
  ordinal,
  selected,
  onClick,
  statusOverride,
  carryoverFromLabel,
  check,
}: LeadRowProps): React.JSX.Element => {
  const created = new Date(lead.createdAt);
  const pax = lead.travellerAdults + lead.travellerChildren + lead.travellerInfants;
  const destination = extractDestination(lead.title);
  // Same hex the status-dot resolves to. We paint it as a 4px left
  // border on the row so the whole card reads as colour-coded at a
  // glance, not just the tiny indicator dot in the corner.
  const stageHex = resolveStageHex(
    statusOverride ? statusOverride.color : lead.currentStatus.color,
    statusOverride ? statusOverride.bucket : lead.currentBucket,
    statusOverride ? statusOverride.name : lead.currentStatus.name,
  );
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={check?.disabled}
      {...(check
        ? {
            role: 'checkbox',
            'aria-checked': check.checked,
            title: check.disabled ? 'Won / Lost - reopen the lead to reassign it' : undefined,
          }
        : {})}
      // Inline `borderLeftColor` carries the stage hex; the static
      // class set still owns `border-l-4` plus the selection / hover
      // bg so Tailwind's purge keeps them.
      style={{ borderLeftColor: stageHex }}
      className={
        'flex w-full items-start gap-2.5 rounded-md border border-l-4 p-2.5 text-left transition-colors ' +
        (check?.disabled ? 'cursor-not-allowed opacity-50 ' : '') +
        ((check ? check.checked : selected)
          ? 'border-blue-200 bg-blue-50'
          : 'border-transparent bg-transparent hover:bg-zinc-50')
      }
    >
      {/* Position number + vertical divider. `self-stretch` lets the
          divider run the full row height; min width keeps 1-2 digit
          rows aligned; tabular-nums stops the column wobbling. In select
          mode the tick box takes the number's slot, so the card does not
          get any wider. */}
      <span className="flex shrink-0 items-stretch gap-2 self-stretch">
        <span className="min-w-4 pt-0.5 text-right text-[13px] font-semibold text-zinc-600 tabular-nums">
          {check ? <CheckBox checked={check.checked} disabled={check.disabled} /> : ordinal}
        </span>
        <span className="w-px bg-zinc-200" aria-hidden />
      </span>
      <UzAvatar name={lead.customer.displayName} size="md" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium text-zinc-900">
            {lead.customer.displayName}
          </span>
          <span className="ml-auto">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={stageDotStyle(
                // When the live master is available, use *its* fields
                // verbatim - including a deliberate NULL bucket (e.g. "Not
                // Qualified" with bucket=null in the master), since `??`
                // would otherwise collapse it onto the lead's denorm
                // (always OPEN/WON/LOST) and break name-inference colors.
                statusOverride ? statusOverride.color : lead.currentStatus.color,
                statusOverride ? statusOverride.bucket : lead.currentBucket,
                statusOverride ? statusOverride.name : lead.currentStatus.name,
              )}
            />
          </span>
        </div>
        <div className="mt-0.5 truncate text-[11.5px] text-zinc-500">
          {destination ?? '-'} · {pax} pax ·{' '}
          {formatBudgetShort(lead.budgetAmount, lead.budgetCurrency)}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[10.5px] text-zinc-400">
          <span>
            {created.toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          {carryoverFromLabel && (
            <span
              className="rounded bg-amber-50 px-1 py-px text-[10px] font-medium text-amber-700 ring-1 ring-amber-200 ring-inset"
              title={`Stage entered in ${carryoverFromLabel} - carried over into this month`}
            >
              from {carryoverFromLabel}
            </span>
          )}
          {isLargeLeadQueryByAdults(lead.travellerAdults) && (
            <span
              className="rounded bg-violet-50 px-1 py-px text-[10px] font-medium text-violet-800 ring-1 ring-violet-200 ring-inset"
              title={`Large lead query - more than ${LARGE_LEAD_ADULTS_THRESHOLD} adults`}
            >
              Large lead
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

// ---------- Helpers ----------

function formatBudgetShort(amount: number | null, currency: string | null | undefined): string {
  if (amount === null || amount === undefined) return '-';
  const code = currency ?? 'INR';
  if (amount >= 1000) {
    return `${code === 'INR' ? '₹' : code} ${Math.round(amount / 1000)}k`;
  }
  return `${code === 'INR' ? '₹' : code} ${amount}`;
}

function extractDestination(title: string): string | null {
  const idx = title.indexOf('→');
  if (idx >= 0) return title.slice(idx + 1).trim();
  return null;
}

/**
 * Short stage label for the chips row (matches u-design's `LEAD_STAGES.short`).
 * Falls back to the full name if no shorter form is found.
 */
function shortStageName(full: string): string {
  const lc = full.toLowerCase();
  if (lc.includes('not qualified')) return 'Not Qualified';
  if (lc === 'active') return 'Active';
  if (lc.includes('contacted') || lc.includes('proposal')) return 'Proposal';
  if (lc.includes('qualified') || lc.includes('warm')) return 'Warm';
  if (lc.includes('booked') || lc.includes('hot')) return 'Hot';
  if (lc.includes('converted') || lc.includes('won')) return 'Won';
  if (lc === 'lost') return 'Lost';
  return full;
}
